import * as Sentry from '@sentry/browser';
import { Icon } from '@subframe/core';
import { updateUpgradePlan } from 'api/frontend';
import {
  useListAddonInstancesInfinite,
  useListClustersInfinite,
  useListUpgradePlansInfinite,
} from 'api/frontend-infinite';
import { Actions, AtomicTooltip } from 'components/design-system';
import { moreTooltipContent } from 'components/design-system/AtomicTooltip';
import BaseTable from 'components/design-system/Table/BaseTable';
import { ConsoleLoader } from 'components/Loader';
import Page from 'components/Page';
import { RouterLink } from 'components/RouterLink';

import { somethingWentWrong, toastAutoHideDuration } from 'constants/toasts';
import useAccountIdRoute from 'hooks/useAccountIdRoute';
import { useAddonIdToNameMap } from 'hooks/useAddonIdToNameMap';
import {
  addonInstanceClusterDisplayLabel,
  useAddonInstanceToClusterMap,
} from 'hooks/useAddonInstanceToClusterMap';
import useUserAccountState from 'hooks/useUserAccountState';
import { useSnackbar } from 'notistack';
import { SyntheticEvent, useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import { UPGRADES } from 'src/data/upgrades';
import { ADDONS } from 'src/data/version_register_addons';
import { Button } from 'subframe/components/Button';
import { InformationalHeader } from 'subframe/components/InformationalHeader';
import { Switch } from 'subframe/components/Switch';
import { Alert, Avatar, Breadcrumbs } from 'subframe/index';
import AnalyticsEventLogger from 'utils/AnalyticsEventLogger';
import styles from '../AcmeUpgradesClusters.module.scss';
import {
  getTooltipForUpgradeCompletedBy,
  getTooltipForUpgradeRequestedBy,
  showCancel,
  getUpgradePlanStatusBadge,
  UpgradeViewMode,
  showArchive,
} from '../helpers/UpgradesHelpers';
import { Searchbar } from 'components/design-system/Searchbar';
import MarkUpgradePlanAsCompletedDialog from '../components/MarkUpgradePlanAsCompletedDialog';
import { filterUpgradePlansByResources } from 'utils/upgrades';
import { clusterFlattenPages, flattenPages } from 'utils/arrays';
import OwnerSelector from '../components/OwnerSelector';
import UpgradesCancelDialog from '../helpers/CancelEntryDialog';
import SkipUpgradeAddonDialog from '../components/SkipUpgradeAddonDialog';
import { ChangeNameDialog } from '../helpers/ChangeNameDialog';
import { UpgradePlan } from 'api/models';
import MultiSelectDropdown from 'components/MultiSelectDropdown';
import { openUpgradeExportInNewWindow } from '../helpers/export_full';
import { useExampleData } from 'hooks/useExampleData';
import { ReloadButton } from 'components/ReloadButton';
import MarkUpgradeAsArchived from '../components/MarkUpgradeAsArchived';

export default function UpgradesAddonIndex() {
  const { logEvent } = AnalyticsEventLogger();
  const { account } = useUserAccountState();
  const basePath = useAccountIdRoute(
    '/orgs/:orgId/accounts/:accountId/upgrades',
  );
  const newClusterPath = useAccountIdRoute(
    '/orgs/:orgId/accounts/:accountId/clusters/new',
  );

  const { enqueueSnackbar } = useSnackbar();

  const [showExampleData, setShowExampleData] = useExampleData();

  const addonIdToNameMap = useAddonIdToNameMap({
    token: account.token,
  });
  const { data: addonData } = useListAddonInstancesInfinite(
    {},
    { request: { headers: { Authorization: `Bearer ${account.token}` } } },
  );
  const { data: upgradesDataPages, mutate } = useListUpgradePlansInfinite(
    {},
    { request: { headers: { Authorization: `Bearer ${account.token}` } } },
  );
  const upgradesData = flattenPages(upgradesDataPages || []) || [];

  // todo: optimize to use same list clusters call for view & clusterNameMap
  const { data: clustersList } = useListClustersInfinite(
    '',
    {},
    {
      request: { headers: { Authorization: `Bearer ${account.token}` } },
    },
    {
      initialSize: 100,
      revalidateFirstPage: false,
    },
  );

  const clusterData = clusterFlattenPages(clustersList || []) || [];
  const realClusters =
    clusterData === undefined
      ? []
      : (clusterData || []).filter(
          (c) => c.internal_k8s_ref !== 'example-cluster-ref',
        );

  useEffect(() => {
    if (clusterData && sessionStorage.getItem('example-data') === null) {
      setShowExampleData(!realClusters.length);
    }
  }, [clusterData]);

  const addonInstanceToClusterMap = useAddonInstanceToClusterMap({
    token: account.token,
    useExampleData: showExampleData,
  });

  const addonInstanceIdToAddonNameMap = showExampleData
    ? new Map(
        ADDONS?.map((a) => {
          return [
            a.id,
            `${addonIdToNameMap.get(a.addon_id) || a.addon_id} ${
              a.version || ''
            }`,
          ];
        }),
      )
    : new Map(
        flattenPages(addonData)?.map((a) => {
          return [
            a.id,
            `${addonIdToNameMap.get(a.addon_id) || a.addon_id} ${
              a.version || ''
            }`,
          ];
        }),
      );

  const upgrades = showExampleData
    ? UPGRADES
    : upgradesData?.sort((a, b) => {
        return a.created > b.created ? -1 : 1; // Latest requested Upgrade Plan should appear on top
      });
  const addonUpgrades =
    upgrades === undefined
      ? undefined
      : filterUpgradePlansByResources(upgrades).addonUpgradePlans;

  const [selected, setSelected] = useState<string[]>([
    'pending',
    'in-progress',
    'completed',
    'outdated',
  ]);
  const [searchUpgrade, setSearchUpgrade] = useState('');

  const handleUpgradesOnChange = (
    event: SyntheticEvent<Element, Event>,
    newValue: string,
  ) => {
    setSearchUpgrade(newValue);
  };

  useDebounce(
    () => {
      if (searchUpgrade) {
        logEvent('search-upgrades', { searchUpgrade });
      }
    },
    1000,
    [searchUpgrade],
  );

  let upgradesToShow = addonUpgrades;
  if (searchUpgrade) {
    upgradesToShow = upgradesToShow?.filter((item) =>
      item?.name.toLowerCase().includes(searchUpgrade.toLowerCase()),
    );
  }
  if (selected.length > 0) {
    upgradesToShow = upgradesToShow?.filter((item) => {
      return selected.includes(item.status);
    });
  }

  const [openEditNameDialog, setOpenEditNameDialog] = useState(false);
  const [openMarkCompletedDialog, setOpenMarkCompletedDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openSkipDialog, setOpenSkipDialog] = useState(false);
  const [openMarkUpgradeAsArchivedDialog, setOpenMarkUpgradeAsArchivedDialog] =
    useState(false);
  const [selectedUpgrade, setSelectedUpgrade] = useState<UpgradePlan>();

  return (
    <Page title="Project Upgrades">
      <div
        className={styles['pageContents']}
        style={{ marginLeft: '32px', width: 'calc(100% - 32px' }}
      >
        <Breadcrumbs>
          <Breadcrumbs.Item>Upgrade Copilot</Breadcrumbs.Item>
          <Breadcrumbs.Divider name="FeatherChevronRight" />
          <Breadcrumbs.Item>Upgrade Plans</Breadcrumbs.Item>
          <Breadcrumbs.Divider name="FeatherChevronRight" />
          <Breadcrumbs.Item active={true}>Projects</Breadcrumbs.Item>
        </Breadcrumbs>
        <div className={styles['header']}>
          <div className={styles['stack']}>
            <span className="text-section-header font-section-header text-default-font">
              Project Upgrade Plans
            </span>
            <ReloadButton reload={mutate} />
          </div>
          <div className={styles['stack-a1f5a859']}>
            <span className={styles['text-79dfdadc']}>
              Overview of all instantiated Project Upgrade Plans
            </span>
            <div className={styles['stack-a4c06eb0']}>
              <span className={styles['text-af9ab9cb']}>Show example data</span>
              <AtomicTooltip
                data-cy="show-example-data-tooltip"
                tooltipContent={
                  showExampleData
                    ? 'Show results from your onboarded clusters'
                    : 'Show example data'
                }
              >
                <Switch
                  data-cy="switch"
                  checked={showExampleData}
                  onCheckedChange={() => {
                    setShowExampleData(!showExampleData);
                    setSearchUpgrade('');
                  }}
                />
              </AtomicTooltip>
            </div>
          </div>
        </div>
        {showExampleData && (
          <Alert
            variant="warning"
            title="You are currently viewing example data"
            description={`To view results from your onboarded clusters, toggle the "Show example data" button`}
          />
        )}
        {(addonData === undefined ||
          upgradesData === undefined ||
          clusterData === undefined) && <ConsoleLoader />}
        {!showExampleData && !!clustersList && realClusters.length === 0 && (
          <div className={styles['stack-435f1a02']}>
            <span className={styles['bodyText']}>
              No clusters onboarded so far. Click here to onboard your first
              cluster
            </span>
            <RouterLink to={newClusterPath}>
              <Button
                size="small"
                icon="FeatherPlus"
                onClick={() =>
                  logEvent('upgrades-addon-onboard-cluster-btn-click')
                }
              >
                Add Cluster
              </Button>
            </RouterLink>
          </div>
        )}
        {!showExampleData &&
          addonData !== undefined &&
          addonData[0].data?.length < 1 &&
          addonIdToNameMap.size > 0 &&
          realClusters.length > 0 && (
            <div className={styles['stack-45320231']}>
              <span className={styles['bodyText']}>
                We have not found any Projects in your clusters so far.
              </span>
            </div>
          )}
        {addonIdToNameMap.size > 0 &&
          (showExampleData ||
            (addonData !== undefined && addonData[0].data?.length > 0)) && (
            <>
              <div className={styles['stack-3227bc77']}>
                <span className={styles['subheaderText']}>Executions</span>
                {addonUpgrades !== undefined && addonUpgrades.length < 1 && (
                  <div className={styles['stack-f4735681']}>
                    <span className={styles['text-0b94c931']}>
                      No Upgrade Plans executed so far.
                    </span>
                  </div>
                )}
                {(addonUpgrades || []).length > 0 && (
                  <>
                    <div className={styles['executionsHeader']}>
                      <InformationalHeader>
                        <InformationalHeader.Item
                          data-cy="total-count"
                          title="Total"
                          bodySlot={
                            <span className="text-body-bold font-body-bold text-default-font">
                              {(addonUpgrades || []).length}
                            </span>
                          }
                        />
                        {(addonUpgrades || []).filter(
                          (upgr) => upgr.status === 'pending',
                        ).length > 0 && (
                          <InformationalHeader.Item
                            data-cy="pending-count"
                            title="Waiting for Plan"
                            bodySlot={
                              <span className="text-body-bold font-body-bold text-default-font">
                                {
                                  (addonUpgrades || []).filter(
                                    (upgr) => upgr.status === 'pending',
                                  ).length
                                }
                              </span>
                            }
                          />
                        )}
                        {(addonUpgrades || []).filter(
                          (upgr) => upgr.status === 'in-progress',
                        ).length > 0 && (
                          <InformationalHeader.Item
                            data-cy="inprogress-count"
                            title="In Progress"
                            bodySlot={
                              <span className="text-body-bold font-body-bold text-default-font">
                                {
                                  (addonUpgrades || []).filter(
                                    (upgr) => upgr.status === 'in-progress',
                                  ).length
                                }
                              </span>
                            }
                          />
                        )}
                        {(addonUpgrades || []).filter(
                          (upgr) => upgr.status === 'skipped',
                        ).length > 0 && (
                          <InformationalHeader.Item
                            data-cy="skipped-count"
                            title="Skipped"
                            bodySlot={
                              <span className="text-body-bold font-body-bold text-default-font">
                                {
                                  (addonUpgrades || []).filter(
                                    (upgr) => upgr.status === 'skipped',
                                  ).length
                                }
                              </span>
                            }
                          />
                        )}
                        {(addonUpgrades || []).filter(
                          (upgr) => upgr.status === 'outdated',
                        ).length > 0 && (
                          <InformationalHeader.Item
                            title="Outdated"
                            bodySlot={
                              <span className="text-body-bold font-body-bold text-default-font">
                                {
                                  (addonUpgrades || []).filter(
                                    (upgr) => upgr.status === 'outdated',
                                  ).length
                                }
                              </span>
                            }
                          />
                        )}
                        {(addonUpgrades || []).filter(
                          (upgr) => upgr.status === 'update-in-progress',
                        ).length > 0 && (
                          <InformationalHeader.Item
                            title="Refreshing Plan"
                            bodySlot={
                              <span className="text-body-bold font-body-bold text-default-font">
                                {
                                  (addonUpgrades || []).filter(
                                    (upgr) =>
                                      upgr.status === 'update-in-progress',
                                  ).length
                                }
                              </span>
                            }
                          />
                        )}
                        {(addonUpgrades || []).filter(
                          (upgr) => upgr.status === 'completed',
                        ).length > 0 && (
                          <InformationalHeader.Item
                            data-cy="completed-count"
                            title="Completed"
                            bodySlot={
                              <span className="text-body-bold font-body-bold text-default-font">
                                {
                                  (addonUpgrades || []).filter(
                                    (upgr) => upgr.status === 'completed',
                                  ).length
                                }
                              </span>
                            }
                          />
                        )}
                        {(addonUpgrades || []).filter(
                          (upgr) => upgr.status === 'cancelled',
                        ).length > 0 && (
                          <InformationalHeader.Item
                            data-cy="cancelled-count"
                            title="Canceled"
                            bodySlot={
                              <span className="text-body-bold font-body-bold text-default-font">
                                {
                                  (addonUpgrades || []).filter(
                                    (upgr) => upgr.status === 'cancelled',
                                  ).length
                                }
                              </span>
                            }
                          />
                        )}
                        {(addonUpgrades || []).filter(
                          (upgr) => upgr.status === 'failed',
                        ).length > 0 && (
                          <InformationalHeader.Item
                            data-cy="failed-count"
                            title="Failed"
                            bodySlot={
                              <span className="text-body-bold font-body-bold text-default-font">
                                {
                                  (addonUpgrades || []).filter(
                                    (upgr) => upgr.status === 'failed',
                                  ).length
                                }
                              </span>
                            }
                          />
                        )}
                        {(addonUpgrades || []).filter(
                          (upgr) => upgr.status === 'rolled-back',
                        ).length > 0 && (
                          <InformationalHeader.Item
                            data-cy="rolledback-count"
                            title="Rolled Back"
                            bodySlot={
                              <span className="text-body-bold font-body-bold text-default-font">
                                {
                                  (addonUpgrades || []).filter(
                                    (upgr) => upgr.status === 'rolled-back',
                                  ).length
                                }
                              </span>
                            }
                          />
                        )}
                      </InformationalHeader>

                      {addonUpgrades?.length ? (
                        <>
                          <MultiSelectDropdown
                            selectAdditionalClassNames="w-64"
                            selectTruncationLength={25}
                            emptyLabel="Status"
                            listHeader="Filter by Status"
                            options={[
                              {
                                value: 'pending',
                                label: 'Waiting for Plan',
                              },
                              { value: 'in-progress', label: 'In Progress' },
                              { value: 'completed', label: 'Completed' },
                              { value: 'skipped', label: 'Skipped' },
                              { value: 'rolled-back', label: 'Rolled Back' },
                              { value: 'failed', label: 'Failed' },
                              { value: 'cancelled', label: 'Canceled' },
                              { value: 'archived', label: 'Archived' },
                            ]}
                            selected={selected}
                            setSelected={setSelected}
                            iconName={
                              selected.length > 0 ? 'FeatherFilter' : null
                            }
                            variant={selected.length > 0 ? 'filled' : 'outline'}
                          />
                          <Searchbar
                            onChange={handleUpgradesOnChange}
                            view={showExampleData ? 'example' : 'real'}
                          />
                        </>
                      ) : (
                        <></>
                      )}
                    </div>
                    <div className={styles['stack-1b1228c7']}>
                      <BaseTable
                        settings={{
                          localStorageKey: 'upgrades_addons_executions',
                          dataCyPrefix: 'upgrades-addons-executions',
                        }}
                        data={upgradesToShow || []}
                        rowLink={(upgr) =>
                          !showExampleData ||
                          upgr.id ===
                            'upgr_54d5a2db-144e-40eb-a828-5757dddec241' ||
                          upgr.id ===
                            'upgr_a5bb9899-1c85-4bc8-925d-b761be654b00' ||
                          upgr.id ===
                            'upgr_ea53db65-3d8d-4744-b8b7-92e3c7552932' ||
                          upgr.id ===
                            'upgr_aec7c666-29b9-4fc5-90dd-5d89514b7a30'
                            ? `${basePath}/addons/${upgr.id}`
                            : ''
                        }
                        rowSettings={{
                          preventClick: (upgr) =>
                            upgr.status === 'pending' ||
                            (showExampleData &&
                              upgr.id !==
                                'upgr_54d5a2db-144e-40eb-a828-5757dddec241' &&
                              upgr.id !==
                                'upgr_a5bb9899-1c85-4bc8-925d-b761be654b00' &&
                              upgr.id !==
                                'upgr_ea53db65-3d8d-4744-b8b7-92e3c7552932' &&
                              upgr.id !==
                                'upgr_aec7c666-29b9-4fc5-90dd-5d89514b7a30'),
                          preventClickToastMessage: showExampleData
                            ? 'This is a demonstration view only at this point - only some Upgrade Plans are available'
                            : 'An Upgrade Plan is being generated for you. You will receive a notification when the Plan is ready.',

                          rowTooltip: (upgr) =>
                            upgr.status === 'pending'
                              ? 'An Upgrade Plan is being generated for you. You will receive a notification when the Plan is ready.'
                              : '',
                        }}
                        noMatchingData={
                          !!upgrades?.length && !upgradesToShow?.length
                        }
                        columns={[
                          {
                            id: '1',
                            title: 'Plan',
                            titleStyle: { width: '25%' },
                            cellType: 'cell',
                            cellStyle: {
                              width: '30%',
                              height: '110px',
                              flex: '0 0 auto',
                            },
                            render: (upgr) => {
                              const resources = (
                                upgr.resources.addon_instances || []
                              ).map((res) =>
                                addonInstanceClusterDisplayLabel(
                                  addonInstanceToClusterMap.get(res.id),
                                  res.id,
                                ),
                              );

                              return (
                                <div className="flex flex-col items-start gap-2">
                                  <div className="flex grow shrink-0 basis-0 items-center justify-center gap-2">
                                    <AtomicTooltip tooltipContent={upgr.name}>
                                      <div className="truncate-ellipsis">
                                        <span className="text-body-bold font-body-bold text-neutral-700">
                                          {upgr.name}
                                        </span>
                                      </div>
                                    </AtomicTooltip>
                                    {upgr.id ===
                                      'upgr_ea53db65-3d8d-4744-b8b7-92e3c7552932' ||
                                    upgr.id ===
                                      'upgr_aec7c666-29b9-4fc5-90dd-5d89514b7a30' ? (
                                      <AtomicTooltip tooltipContent="AI context generated">
                                        <Icon
                                          className="text-subheader font-subheader text-brand-primary"
                                          name="FeatherSparkles"
                                        />
                                      </AtomicTooltip>
                                    ) : null}
                                  </div>
                                  {upgr.current_version ? (
                                    <div className="flex items-start gap-2">
                                      {upgr.current_version ? (
                                        <span className="text-label font-label text-subtext-color">
                                          {`From: ${upgr.current_version}`}
                                        </span>
                                      ) : (
                                        <></>
                                      )}
                                      {upgr.desired_version ? (
                                        <span className="text-label font-label text-subtext-color">
                                          {`To: ${upgr.desired_version}`}
                                        </span>
                                      ) : (
                                        <></>
                                      )}
                                    </div>
                                  ) : (
                                    <></>
                                  )}
                                  {resources.length > 0 ? (
                                    <div className="flex items-start gap-2">
                                      <span className="text-label font-label text-subtext-color">
                                        Clusters:{' '}
                                        {resources.slice(0, 1).join(', ')}
                                      </span>
                                      {resources.length > 1 && (
                                        <AtomicTooltip
                                          tooltipContent={moreTooltipContent(
                                            resources,
                                            1,
                                          )}
                                        >
                                          <span
                                            className={styles['text-cfa53551']}
                                          >
                                            +{Math.max(0, resources.length - 1)}{' '}
                                            more
                                          </span>
                                        </AtomicTooltip>
                                      )}
                                    </div>
                                  ) : (
                                    <></>
                                  )}
                                </div>
                              );
                            },
                          },
                          {
                            id: '2',
                            title: 'Status',
                            titleStyle: { width: '10%' },
                            cellType: 'badge-cell',
                            cellProperties: {
                              variant: (upgr) => {
                                return getUpgradePlanStatusBadge(upgr.status)
                                  .variant;
                              },
                            },
                            render: (upgr) => {
                              return getUpgradePlanStatusBadge(upgr.status)
                                .title;
                            },
                          },
                          {
                            id: '3',
                            title: 'Projects',
                            titleStyle: { width: '15%' },
                            cellType: 'cell',
                            render: (upgr) => {
                              const resources = (
                                upgr.resources.addon_instances || []
                              ).map(
                                (res) =>
                                  addonInstanceIdToAddonNameMap.get(res.id) ||
                                  res.id,
                              );

                              return (
                                <>
                                  <span
                                    className={styles['labelText']}
                                    style={{
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'break-spaces',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    {resources.slice(0, 1).join(', ')}
                                  </span>
                                  {resources.length > 1 && (
                                    <AtomicTooltip
                                      tooltipContent={moreTooltipContent(
                                        resources,
                                        1,
                                      )}
                                    >
                                      <span className={styles['text-cfa53551']}>
                                        +{Math.max(0, resources.length - 1)}{' '}
                                        more
                                      </span>
                                    </AtomicTooltip>
                                  )}
                                </>
                              );
                            },
                          },
                          {
                            id: '4',
                            title: 'Owner',
                            cellType: 'cell',
                            cellStyle: {
                              width: '10%',
                            },
                            render: (upgr) => {
                              return (
                                <>
                                  {upgr.owner?.id ? (
                                    <AtomicTooltip
                                      tooltipContent={upgr.owner?.name}
                                    >
                                      {upgr.owner?.picture ? (
                                        <Avatar
                                          size="small"
                                          image={upgr.owner?.picture}
                                          className="ph-no-capture"
                                        />
                                      ) : (
                                        <Avatar variant="neutral" size="small">
                                          {upgr.owner?.name
                                            ?.charAt(0)
                                            ?.toUpperCase()}
                                        </Avatar>
                                      )}
                                    </AtomicTooltip>
                                  ) : (
                                    <></>
                                  )}
                                </>
                              );
                            },
                          },
                          {
                            id: '5',
                            title: 'Requested by',
                            titleStyle: { width: '15%' },
                            cellType: 'cell',
                            render: (upgr) => {
                              return (
                                <>
                                  {upgr.requested_by?.name && upgr.created ? (
                                    <AtomicTooltip
                                      className="ph-no-capture"
                                      tooltipContent={getTooltipForUpgradeRequestedBy(
                                        upgr.requested_by?.name,
                                        upgr.created,
                                        UpgradeViewMode.Plan,
                                      )}
                                    >
                                      {upgr.requested_by?.picture ? (
                                        <Avatar
                                          size="small"
                                          image={upgr.requested_by?.picture}
                                          className="ph-no-capture"
                                        />
                                      ) : (
                                        <Avatar variant="neutral" size="small">
                                          {upgr.requested_by?.name
                                            ?.charAt(0)
                                            ?.toUpperCase()}
                                        </Avatar>
                                      )}
                                    </AtomicTooltip>
                                  ) : (
                                    <span className={styles['bodyText']}></span>
                                  )}
                                </>
                              );
                            },
                          },
                          {
                            id: '6',
                            title: 'Completed/Skipped by',
                            titleStyle: { width: '15%' },
                            cellType: 'cell',
                            render: (upgr) => {
                              return (
                                <div className="flex grow shrink-0 basis-0 items-start gap-2">
                                  {upgr.completed_by?.name && upgr.updated ? (
                                    <>
                                      <AtomicTooltip
                                        tooltipContent={getTooltipForUpgradeCompletedBy(
                                          upgr.completed_by?.name,
                                          upgr.updated,
                                        )}
                                        className="ph-no-capture"
                                      >
                                        {upgr.completed_by?.picture ? (
                                          <Avatar
                                            size="small"
                                            image={upgr.completed_by?.picture}
                                            className="ph-no-capture"
                                          />
                                        ) : (
                                          <Avatar
                                            variant="neutral"
                                            size="small"
                                          >
                                            {upgr.completed_by?.name
                                              ?.charAt(0)
                                              ?.toUpperCase()}
                                          </Avatar>
                                        )}
                                      </AtomicTooltip>
                                    </>
                                  ) : (
                                    <span className={styles['bodyText']}></span>
                                  )}
                                </div>
                              );
                            },
                          },
                        ]}
                        actions={[
                          {
                            id: 'actions',
                            render: (upgr) => {
                              const markdownExports = (
                                upgr.exports || []
                              ).filter((e) => e.format === 'markdown');

                              return (
                                <div className={styles['stack-1b79c295']}>
                                  <div
                                    className={styles['stack-1c3bdfbc']}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                    }}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                    }}
                                  >
                                    <Actions
                                      sections={[
                                        {
                                          title: 'Update Properties',
                                          items: [
                                            <Actions.Item
                                              key="edit-name"
                                              title="Edit Name"
                                              icon="FeatherEdit2"
                                              onClick={() => {
                                                setSelectedUpgrade(upgr);
                                                setOpenEditNameDialog(true);
                                              }}
                                              disabled={showExampleData}
                                              tooltip={
                                                showExampleData
                                                  ? 'Example plans cannot be updated'
                                                  : ''
                                              }
                                            />,

                                            <OwnerSelector
                                              key="change-owner"
                                              onConfirm={async (newOwner) => {
                                                if (showExampleData) {
                                                  enqueueSnackbar(
                                                    'Example plans cannot be updated',
                                                    {
                                                      variant: 'warning',
                                                      autoHideDuration:
                                                        toastAutoHideDuration,
                                                    },
                                                  );
                                                } else {
                                                  try {
                                                    await updateUpgradePlan(
                                                      upgr.id,
                                                      {
                                                        owner: { id: newOwner },
                                                      },
                                                      {
                                                        headers: {
                                                          Authorization: `Bearer ${account.token}`,
                                                        },
                                                      },
                                                    );
                                                    logEvent(
                                                      'upgrades-clusters-plan-owner-change',
                                                      {
                                                        planId: upgr.id,
                                                        ownerId: newOwner,
                                                      },
                                                    );
                                                    mutate();
                                                    enqueueSnackbar(
                                                      'Successfully updated the owner of the Upgrade Plan',
                                                      {
                                                        variant: 'success',
                                                        autoHideDuration:
                                                          toastAutoHideDuration,
                                                      },
                                                    );
                                                  } catch (err) {
                                                    enqueueSnackbar(
                                                      somethingWentWrong.replace(
                                                        '<action>',
                                                        'updating the owner of the Upgrade Plan',
                                                      ),
                                                      {
                                                        variant: 'error',
                                                        autoHideDuration:
                                                          toastAutoHideDuration,
                                                      },
                                                    );
                                                    Sentry.captureException(
                                                      err,
                                                    );
                                                  }
                                                }
                                              }}
                                            />,
                                          ],
                                        },
                                        {
                                          title: 'Workflow Actions',
                                          items: [
                                            <Actions.Item
                                              key="preverify"
                                              title="Preverify"
                                              icon="FeatherShieldCheck"
                                              disabled={true}
                                              preview={true}
                                            />,
                                          ],
                                        },
                                        {
                                          title: 'Lifecycle Actions',
                                          items: [
                                            <Actions.Item
                                              key="skip-upgrade"
                                              title="Skip Upgrade Plan"
                                              icon="FeatherSkipForward"
                                              onClick={() => {
                                                setOpenSkipDialog(true);
                                                setSelectedUpgrade(upgr);
                                              }}
                                              disabled={
                                                showExampleData ||
                                                upgr.status !== 'in-progress'
                                              }
                                              tooltip={
                                                showExampleData
                                                  ? 'Example plans cannot be skipped'
                                                  : upgr.status !==
                                                    'in-progress'
                                                  ? 'Action is not available for this Upgrade Plan'
                                                  : ''
                                              }
                                            />,

                                            <Actions.Item
                                              key="mark-completed"
                                              title="Mark as Completed"
                                              icon="FeatherCheck"
                                              onClick={() => {
                                                setSelectedUpgrade(upgr);
                                                setOpenMarkCompletedDialog(
                                                  true,
                                                );
                                              }}
                                              disabled={
                                                showExampleData ||
                                                upgr.status !== 'in-progress'
                                              }
                                              tooltip={
                                                showExampleData
                                                  ? 'Example plans cannot be updated'
                                                  : upgr.status !==
                                                    'in-progress'
                                                  ? 'Action is only available for In Progress Upgrade Plans'
                                                  : ''
                                              }
                                            />,
                                            <Actions.Item
                                              key="cancel-plan"
                                              title="Cancel Plan"
                                              icon="FeatherX"
                                              onClick={() => {
                                                setSelectedUpgrade(upgr);
                                                setOpenCancelDialog(true);
                                              }}
                                              disabled={
                                                showExampleData ||
                                                !showCancel(upgr)
                                              }
                                              tooltip={
                                                showExampleData
                                                  ? 'Example plans cannot be canceled'
                                                  : !showCancel(upgr)
                                                  ? 'Action is not available for this Upgrade Plan'
                                                  : ''
                                              }
                                            />,
                                            <Actions.Item
                                              key="mark-archived"
                                              title="Archive"
                                              icon="FeatherArchive"
                                              onClick={() => {
                                                setSelectedUpgrade(upgr);
                                                setOpenMarkUpgradeAsArchivedDialog(
                                                  true,
                                                );
                                              }}
                                              disabled={
                                                showExampleData ||
                                                !showArchive(upgr)
                                              }
                                              tooltip={
                                                showExampleData
                                                  ? 'Action is not available on example data'
                                                  : !showArchive(upgr)
                                                  ? 'Action is not available for this Upgrade Plan'
                                                  : ''
                                              }
                                            />,
                                          ],
                                        },
                                        {
                                          title: 'Export',
                                          items: [
                                            <Actions.Item
                                              key="export-as-markdown"
                                              title="Export as Markdown"
                                              icon="FeatherCode2"
                                              onClick={() => {
                                                openUpgradeExportInNewWindow(
                                                  UpgradeViewMode.Plan,
                                                  upgr.id,
                                                  account.token,
                                                  enqueueSnackbar,
                                                );
                                              }}
                                              disabled={
                                                showExampleData ||
                                                markdownExports.length < 1
                                              }
                                              tooltip={
                                                showExampleData
                                                  ? 'Example Upgrade Plans cannot be exported'
                                                  : markdownExports.length < 1
                                                  ? 'Action is not available for this Upgrade Plan'
                                                  : ''
                                              }
                                            />,
                                          ],
                                        },
                                      ]}
                                    />
                                  </div>

                                  {upgr.status !== 'pending' && (
                                    <Icon
                                      className={styles['bodyText']}
                                      name="FeatherChevronRight"
                                    />
                                  )}
                                  {upgr.status === 'pending' && (
                                    <Icon
                                      className={styles['icon-ef8c001b']}
                                      name="FeatherChevronRight"
                                    />
                                  )}
                                </div>
                              );
                            },
                          },
                        ]}
                      />
                      <ChangeNameDialog
                        key="edit-name"
                        open={openEditNameDialog}
                        setOpen={setOpenEditNameDialog}
                        mode={UpgradeViewMode.Plan}
                        name={selectedUpgrade?.name || ''}
                        onConfirm={async (newName) => {
                          if (showExampleData) {
                            enqueueSnackbar('Example plans cannot be updated', {
                              variant: 'warning',
                              autoHideDuration: toastAutoHideDuration,
                            });
                          } else {
                            try {
                              await updateUpgradePlan(
                                selectedUpgrade?.id || '',
                                { name: newName },
                                {
                                  headers: {
                                    Authorization: `Bearer ${account.token}`,
                                  },
                                },
                              );
                              logEvent('upgrades-clusters-plan-name-change', {
                                planId: selectedUpgrade?.id || '',
                                name: newName,
                              });
                              mutate();
                              enqueueSnackbar(
                                'Successfully edited the requested properties of the Upgrade Plan',
                                {
                                  variant: 'success',
                                  autoHideDuration: toastAutoHideDuration,
                                },
                              );
                            } catch (err) {
                              enqueueSnackbar(
                                somethingWentWrong.replace(
                                  '<action>',
                                  'updating requested properties of the Upgrade Plan',
                                ),
                                {
                                  variant: 'error',
                                  autoHideDuration: toastAutoHideDuration,
                                },
                              );
                              Sentry.captureException(err);
                            }
                          }
                        }}
                      />
                      <SkipUpgradeAddonDialog
                        key="skip-upgrade"
                        upgradeId={selectedUpgrade?.id || ''}
                        mutatePlan={() => {
                          mutate();
                        }}
                        open={openSkipDialog}
                        onClose={() => setOpenSkipDialog(false)}
                      />
                      <UpgradesCancelDialog
                        open={openCancelDialog}
                        setOpen={setOpenCancelDialog}
                        mode={UpgradeViewMode.Plan}
                        onConfirm={async () => {
                          if (showExampleData) {
                            enqueueSnackbar(
                              'Example plans cannot be canceled',
                              {
                                variant: 'warning',
                                autoHideDuration: toastAutoHideDuration,
                              },
                            );
                          } else {
                            try {
                              await updateUpgradePlan(
                                selectedUpgrade?.id || '',
                                { status: 'cancelled' },
                                {
                                  headers: {
                                    Authorization: `Bearer ${account.token}`,
                                  },
                                },
                              );
                              logEvent('upgrades-clusters-plan-cancel', {
                                planId: selectedUpgrade?.id || '',
                                name: selectedUpgrade?.name || '',
                              });
                              mutate();
                              enqueueSnackbar(
                                'Successfully canceled Upgrade Plan',
                                {
                                  variant: 'success',
                                  autoHideDuration: toastAutoHideDuration,
                                },
                              );
                            } catch (err) {
                              enqueueSnackbar(
                                somethingWentWrong.replace(
                                  '<action>',
                                  'canceling this Upgrade Plan',
                                ),
                                {
                                  variant: 'error',
                                  autoHideDuration: toastAutoHideDuration,
                                },
                              );
                              Sentry.captureException(err);
                            }
                          }
                        }}
                      />
                      <MarkUpgradePlanAsCompletedDialog
                        upgradeId={selectedUpgrade?.id || ''}
                        mutatePlan={() => mutate()}
                        open={openMarkCompletedDialog}
                        onClose={() => setOpenMarkCompletedDialog(false)}
                      />
                      <MarkUpgradeAsArchived
                        upgradeId={selectedUpgrade?.id || ''}
                        mutateUpgrade={mutate}
                        open={openMarkUpgradeAsArchivedDialog}
                        onClose={() =>
                          setOpenMarkUpgradeAsArchivedDialog(false)
                        }
                      />
                    </div>
                  </>
                )}
              </div>
            </>
          )}
      </div>
    </Page>
  );
}
